import { CodeBlock, CodeCopyButton } from '@squareup/dex-ui';
import { MarkdownInlineCode } from '@squareup/dex-ui-shared-markdown-components';
import type { HighlightLanguage } from '@squareup/dex-utils-highlight';
import React, { FC, PropsWithChildren, isValidElement } from 'react';

interface MdxPreProps {
  children?: React.ReactNode;
}

const MdxMdxCodeBlock: FC<MdxPreProps> = ({ children }) => {
  // MDX wraps code blocks in <pre><code className="language-X">...</code></pre>
  // Extract the language and content from the child <code> element
  if (isValidElement(children)) {
    const props = children.props as Record<string, unknown>;
    const className = (props.className as string) || '';
    const language = (className.replace('language-', '') ||
      'bash') as HighlightLanguage;
    const content =
      typeof props.children === 'string'
        ? props.children.trimEnd()
        : String(props.children || '');

    return (
      <CodeBlock
        code={content}
        language={language}
        showLineNumbers={true}
        actionButtonGroup={<CodeCopyButton code={content} />}
      />
    );
  }

  return <pre>{children}</pre>;
};

MdxMdxCodeBlock.displayName = 'MdxCodeBlock';

interface MdxCodeProps {
  children?: React.ReactNode;
  className?: string;
}

const MdxMdxInlineCode: FC<PropsWithChildren<MdxCodeProps>> = ({
  children,
  className,
}) => {
  // If className starts with 'language-', this is inside a <pre> and
  // will be handled by MdxMdxCodeBlock. Just pass through.
  if (className?.startsWith('language-')) {
    return <code className={className}>{children}</code>;
  }

  const content =
    typeof children === 'string' ? children : String(children || '');
  return <MarkdownInlineCode content={content} />;
};

MdxMdxInlineCode.displayName = 'MdxInlineCode';

export { MdxMdxCodeBlock, MdxMdxInlineCode };
